import {
  type UnknownRecord,
  hasOwn,
  fail,
  identifierAt,
  exactObject,
  nonBlankTextAt,
  textAt,
  enumAt,
  valueTextAt,
  numberAt,
  literalAt,
  assertJsonByteSize,
  MAX_DOCUMENT_PAYLOAD_BYTES,
  objectParser,
  bucketNameAt,
  visibilityAt,
  bucketOriginAt,
  positiveIntegerAt,
  booleanAt,
  timestampAt,
  objectAt,
  nonBlankText,
  MAX_MESSAGE_TEXT,
  nullable,
  oneOf,
  arrayOf,
  literal,
  text,
  boundedIntegerAt,
  integerAt
} from './primitives';
import {
  MAX_DOCUMENT_PROPERTIES,
  MAX_DOCUMENT_NAME_LENGTH,
  MAX_DOCUMENT_LABEL_LENGTH,
  BUCKET_VALUE_KINDS,
  MAX_DOCUMENT_DEPTH,
  MAX_DOCUMENT_ENTITIES,
  BUCKET_DOCUMENT_VERSION,
  type BucketDocument,
  type BucketDocumentDraft,
  type SmartFillBucket,
  type UnavailableBucket,
  type BucketListEntry,
  type Workspace,
  WORKSPACE_STATES,
  type WorkspaceConversion,
  type FillSource,
  type WorkspaceUsers,
  type BucketProfile,
  type BucketProfileFact
} from '../dtos';
import {
  type BucketListPayload,
  type BucketGetPayload,
  type BucketCreatePayload,
  type BucketUpdatePayload,
  type BucketDeletePayload,
  type BucketRevertLearningPayload,
  type WorkspaceListPayload,
  type WorkspaceConvertPayload,
  type ActiveBucketSetPayload
} from '../protocol';

const PARSEABLE_BOOLEAN_VALUES = new Set([
  'true',
  'yes',
  'y',
  '1',
  'checked',
  'false',
  'no',
  'n',
  '0',
  'unchecked'
]);

/** Shared walk state for one document validation pass. */
interface DocumentWalkState {
  /** Node IDs are required (server results) or optional (sidebar drafts). */
  requireIds: boolean;
  ids: Set<string>;
  entities: number;
  properties: number;
}

function documentNodeIdAt(
  object: UnknownRecord,
  path: string,
  state: DocumentWalkState
): string | null {
  if (!hasOwn(object, 'id')) {
    if (state.requireIds) fail(`${path}.id`, 'missing required property');
    return null;
  }
  const id = identifierAt(object.id, `${path}.id`);
  if (state.ids.has(id)) fail(`${path}.id`, `duplicate document node ID ${JSON.stringify(id)}`);
  state.ids.add(id);
  return id;
}

function documentPropertyAt(object: unknown, path: string, state: DocumentWalkState): void {
  state.properties += 1;
  if (state.properties > MAX_DOCUMENT_PROPERTIES) {
    fail(path, `document exceeds ${MAX_DOCUMENT_PROPERTIES} properties`);
  }
  const property = exactObject(
    object,
    path,
    ['semanticKey', 'kind', 'value'],
    ['id', 'displayLabel', 'sourceConfidence']
  );
  documentNodeIdAt(property, path, state);
  nonBlankTextAt(property.semanticKey, `${path}.semanticKey`, MAX_DOCUMENT_NAME_LENGTH);
  if (hasOwn(property, 'displayLabel')) {
    textAt(property.displayLabel, `${path}.displayLabel`, MAX_DOCUMENT_LABEL_LENGTH);
  }
  const kind = enumAt(property.kind, BUCKET_VALUE_KINDS, `${path}.kind`);
  const propertyValue = valueTextAt(property.value, `${path}.value`);
  if (hasOwn(property, 'sourceConfidence')) {
    numberAt(property.sourceConfidence, `${path}.sourceConfidence`, 0, 1);
  }
  if (
    kind === 'BOOLEAN' &&
    propertyValue.trim() !== '' &&
    !PARSEABLE_BOOLEAN_VALUES.has(propertyValue.trim().toLowerCase())
  ) {
    fail(`${path}.value`, 'BOOLEAN properties must hold a parseable boolean');
  }
}

function documentEntityAt(
  object: unknown,
  path: string,
  isRoot: boolean,
  depth: number,
  state: DocumentWalkState
): void {
  if (depth > MAX_DOCUMENT_DEPTH) fail(path, `document exceeds depth ${MAX_DOCUMENT_DEPTH}`);
  state.entities += 1;
  if (state.entities > MAX_DOCUMENT_ENTITIES) {
    fail(path, `document exceeds ${MAX_DOCUMENT_ENTITIES} entities`);
  }
  const entity = exactObject(
    object,
    path,
    ['type'],
    ['id', 'relation', 'role', 'label', 'properties', 'children']
  );
  documentNodeIdAt(entity, path, state);
  nonBlankTextAt(entity.type, `${path}.type`, MAX_DOCUMENT_NAME_LENGTH);
  if (isRoot) {
    if (hasOwn(entity, 'relation') && String(entity.relation).trim() !== '') {
      fail(`${path}.relation`, 'the root entity must not declare a relation');
    }
  } else {
    nonBlankTextAt(entity.relation, `${path}.relation`, MAX_DOCUMENT_NAME_LENGTH);
  }
  if (hasOwn(entity, 'role')) textAt(entity.role, `${path}.role`, MAX_DOCUMENT_NAME_LENGTH);
  if (hasOwn(entity, 'label')) textAt(entity.label, `${path}.label`, MAX_DOCUMENT_LABEL_LENGTH);

  if (hasOwn(entity, 'properties')) {
    if (!Array.isArray(entity.properties)) fail(`${path}.properties`, 'expected an array');
    const semanticKeys = new Set<string>();
    entity.properties.forEach((item, index) => {
      const itemPath = `${path}.properties[${index}]`;
      documentPropertyAt(item, itemPath, state);
      const semanticKey = String((item as UnknownRecord).semanticKey).trim();
      if (semanticKeys.has(semanticKey)) {
        fail(
          itemPath,
          `entity declares property semantic key ${JSON.stringify(semanticKey)} twice`
        );
      }
      semanticKeys.add(semanticKey);
    });
  }
  if (hasOwn(entity, 'children')) {
    if (!Array.isArray(entity.children)) fail(`${path}.children`, 'expected an array');
    entity.children.forEach((item, index) => {
      documentEntityAt(item, `${path}.children[${index}]`, false, depth + 1, state);
    });
  }
}

function documentAt(value: unknown, path: string, requireIds: boolean): void {
  const object = exactObject(value, path, ['version', 'root']);
  literalAt(object.version, BUCKET_DOCUMENT_VERSION, `${path}.version`);
  const state: DocumentWalkState = { requireIds, ids: new Set(), entities: 0, properties: 0 };
  documentEntityAt(object.root, `${path}.root`, true, 1, state);
  assertJsonByteSize(value, path, MAX_DOCUMENT_PAYLOAD_BYTES);
}

/** Parses a complete server-owned document; every node carries an ID. */
function parseBucketDocument(value: unknown, path = '$'): BucketDocument {
  documentAt(value, path, true);
  return value as BucketDocument;
}

/** Parses a sidebar draft document; new nodes omit IDs for the server to mint. */
export function parseBucketDocumentDraft(value: unknown, path = '$'): BucketDocumentDraft {
  documentAt(value, path, false);
  return value as BucketDocumentDraft;
}

export const parseSmartFillBucket = objectParser<SmartFillBucket>(
  {
    id: identifierAt,
    operationId: identifierAt,
    name: bucketNameAt,
    visibility: visibilityAt,
    origin: bucketOriginAt,
    document: parseBucketDocument,
    revision: positiveIntegerAt,
    canEdit: booleanAt,
    createdAt: timestampAt,
    updatedAt: timestampAt
  },
  { optional: { hasLearnedChanges: booleanAt } }
);

export const parseUnavailableBucket = objectParser<UnavailableBucket>({
  unavailable: (value, path) => literalAt(value, true, path),
  id: identifierAt,
  name: bucketNameAt
});

export function parseBucketListEntry(value: unknown, path: string): BucketListEntry {
  const object = objectAt(value, path);
  return hasOwn(object, 'unavailable')
    ? parseUnavailableBucket(value, path)
    : parseSmartFillBucket(value, path);
}

export const parseWorkspace = objectParser<Workspace>({
  id: identifierAt,
  title: nonBlankText(MAX_MESSAGE_TEXT),
  clientName: nullable(nonBlankTextAt),
  state: oneOf(WORKSPACE_STATES),
  updatedAt: timestampAt
});

export const parseWorkspaceConversion = objectParser<WorkspaceConversion>({
  operationId: identifierAt,
  bucketIds: arrayOf(identifierAt),
  completed: booleanAt
});

// The site profile's own naming rules, which every profile fact satisfies.
const PROFILE_NAME_PATTERN = /^[a-z][a-z0-9_]{0,39}$/;
const PROFILE_KEY_PATTERN = /^[a-z][a-z0-9_]{0,79}$/;
const RELATION_PATH_PATTERN = /^([a-z][a-z0-9_]{0,39}(\/[a-z][a-z0-9_]{0,39}){0,5})?$/;

const matchingAt =
  (pattern: RegExp, expected: string) =>
  (value: unknown, path: string): string => {
    const matched = textAt(value, path, MAX_DOCUMENT_NAME_LENGTH);
    if (!pattern.test(matched)) fail(path, `expected ${expected}`);
    return matched;
  };

export const parseBucketProfileFact = objectParser<BucketProfileFact>({
  ownerType: matchingAt(PROFILE_NAME_PATTERN, 'a lowercase entity type'),
  relationPath: matchingAt(RELATION_PATH_PATTERN, 'relations joined by "/"'),
  semanticKey: matchingAt(PROFILE_KEY_PATTERN, 'a lowercase property key'),
  label: nonBlankText(MAX_DOCUMENT_LABEL_LENGTH),
  kind: oneOf(BUCKET_VALUE_KINDS),
  carrierCount: positiveIntegerAt
});

export const parseBucketProfile = objectParser<BucketProfile>({
  id: identifierAt,
  name: nonBlankTextAt,
  carriers: arrayOf(nonBlankTextAt),
  facts: arrayOf(parseBucketProfileFact)
});

export const parseFillSource = objectParser<FillSource>({
  kind: literal('BUCKET'),
  bucketId: identifierAt,
  label: nonBlankTextAt
});

export const listPayloadAt = objectParser<BucketListPayload>(
  {},
  { optional: { query: text(200), limit: boundedIntegerAt(100) } }
);

export const bucketGetPayloadAt = objectParser<BucketGetPayload>({ bucketId: identifierAt });

export const bucketCreatePayloadAt = objectParser<BucketCreatePayload>({
  operationId: identifierAt,
  name: bucketNameAt,
  visibility: visibilityAt,
  document: parseBucketDocumentDraft
});

export function bucketUpdatePayloadAt(value: unknown, path: string): BucketUpdatePayload {
  const object = exactObject(
    value,
    path,
    ['bucketId', 'expectedRevision'],
    ['name', 'visibility', 'document']
  );
  identifierAt(object.bucketId, `${path}.bucketId`);
  if (integerAt(object.expectedRevision, `${path}.expectedRevision`) < 1) {
    fail(`${path}.expectedRevision`, 'must be positive');
  }
  if (!hasOwn(object, 'name') && !hasOwn(object, 'visibility') && !hasOwn(object, 'document')) {
    fail(path, 'must include at least one bucket change');
  }
  if (hasOwn(object, 'name')) {
    bucketNameAt(object.name, `${path}.name`);
  }
  if (hasOwn(object, 'visibility')) visibilityAt(object.visibility, `${path}.visibility`);
  if (hasOwn(object, 'document')) {
    parseBucketDocumentDraft(object.document, `${path}.document`);
  }
  return value as BucketUpdatePayload;
}

export function bucketDeletePayloadAt(value: unknown, path: string): BucketDeletePayload {
  return bucketGetPayloadAt(value, path);
}

export const bucketRevertLearningPayloadAt = objectParser<BucketRevertLearningPayload>({
  bucketId: identifierAt,
  expectedRevision: positiveIntegerAt
});

export const workspaceListPayloadAt = objectParser<WorkspaceListPayload>(
  {},
  { optional: { query: text(200), limit: boundedIntegerAt(100), userId: nullable(identifierAt) } }
);

export const workspaceUsersAt = objectParser<WorkspaceUsers>({
  currentUserId: identifierAt,
  users: arrayOf(objectParser({ id: identifierAt, name: text(256), email: text(320) }))
});

export const workspaceConvertPayloadAt = objectParser<WorkspaceConvertPayload>({
  workspaceId: identifierAt,
  operationId: identifierAt
});

export const activeBucketSetPayloadAt = objectParser<ActiveBucketSetPayload>({
  bucketId: nullable(identifierAt)
});
